<template>
  <div class="flex flex-col h-full">
    <!-- Day header -->
    <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] sticky top-0 z-10" :class="allDayBookings.length || dayTasks.length ? '' : 'border-b border-gray-200 dark:border-gray-700'">
      <div class="flex">
        <!-- Time gutter — timezone labels -->
        <div class="flex-shrink-0 flex items-end pb-1" :class="showSecondary ? 'w-24' : 'w-14'">
          <div v-if="showSecondary" class="flex-1 flex items-end justify-end pr-1">
            <span class="text-[11px] font-semibold text-indigo-400 leading-none">
              {{ secondaryTzLabel }}
            </span>
          </div>
          <div class="w-14 flex-shrink-0 flex items-end justify-end pr-2">
            <span v-if="primaryTzLabel"
                  class="text-[11px] font-semibold text-gray-600 dark:text-gray-400 leading-none">
              {{ primaryTzLabel }}
            </span>
          </div>
        </div>
        <div class="flex-1 text-center py-2">
          <p class="text-[13px] font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide">
            {{ weekdayLabel }}
          </p>
          <div
            class="w-10 h-10 mx-auto mt-0.5 flex items-center justify-center rounded-full text-[22px] font-semibold"
            :class="isToday ? 'bg-blue-600 text-white' : 'text-gray-700 dark:text-gray-300'"
          >
            {{ dayNum }}
          </div>
        </div>
      </div>
    </div>

    <!-- All-day events row -->
    <div
      v-if="allDayBookings.length || dayTasks.length"
      class="flex-shrink-0 flex border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128]"
    >
      <div class="flex-shrink-0" :class="showSecondary ? 'w-24' : 'w-14'"></div>
      <div class="flex-1 min-w-0 border-l border-gray-100 dark:border-gray-800 p-0.5 space-y-0.5 min-h-[26px]">
        <div
          v-for="booking in allDayBookings"
          :key="booking.id"
          class="text-[12px] font-medium truncate px-1.5 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity"
          :style="{
            backgroundColor: darkenColor(booking.color_code || '#3b82f6'),
            color: readableTextColor(booking.color_code || '#3b82f6'),
          }"
          @click="emit('event-click', booking, $event.currentTarget.getBoundingClientRect())"
        >
          {{ booking.title }}
        </div>
        <CalendarTaskSummary
          :tasks="dayTasks"
          @open="openTask"
        />
      </div>
    </div>

    <!-- Scrollable time grid -->
    <div class="flex-1 overflow-y-auto" ref="gridEl">
      <div class="flex" style="height: 1536px;">
        <!-- Hour labels (primary + optional secondary TZ) -->
        <div class="flex-shrink-0 flex" :class="showSecondary ? 'w-24' : 'w-14'">
          <!-- Secondary TZ labels -->
          <div v-if="showSecondary" class="relative w-10">
            <div
              v-for="hour in 24"
              :key="hour"
              class="absolute w-full text-right pr-1"
              :style="{ top: `${(hour - 1) * ROW_HEIGHT - 8}px` }"
            >
              <span v-if="hour > 1" class="text-[12px] text-indigo-400">
                {{ secondaryHourLabels[hour - 1] }}
              </span>
            </div>
          </div>
          <!-- Primary TZ labels -->
          <div class="relative w-14">
            <div
              v-for="hour in 24"
              :key="hour"
              class="absolute w-full text-right pr-2"
              :style="{ top: `${(hour - 1) * ROW_HEIGHT - 8}px` }"
            >
              <span v-if="hour > 1" class="text-[12px] text-gray-600 dark:text-gray-400">
                {{ hourLabels[hour - 1] }}
              </span>
            </div>
          </div>
        </div>

        <!-- Single day column -->
        <div
          ref="columnEl"
          :data-day-key="columnDayKey"
          class="flex-1 relative border-l border-gray-200 dark:border-gray-800 touch-none"
          :class="isToday ? 'bg-blue-50/30 dark:bg-blue-950/10' : 'bg-white dark:bg-[#0d1117]'"
          @click="onSlotClick"
          @pointerdown="onGridPointerDown"
        >
          <div
            v-for="hour in 24"
            :key="hour"
            class="absolute left-0 right-0 border-t border-gray-200 dark:border-gray-800"
            :style="{ top: `${(hour - 1) * ROW_HEIGHT}px` }"
          ></div>
          <div
            v-for="hour in 24"
            :key="`h-${hour}`"
            class="absolute left-0 right-0 border-t border-gray-100 dark:border-gray-900"
            style="border-style: dashed;"
            :style="{ top: `${(hour - 1) * ROW_HEIGHT + ROW_HEIGHT / 2}px` }"
          ></div>

          <CalendarCurrentTimeLine v-if="isToday && settingsStore.show_now_indicator" />

          <CalendarEventCard
            v-for="segment in daySegments"
            :dragging="gridDrag.isBeingDragged(segment.booking.id)"
            :key="segment.key"
            :segment="segment"
            :position="positionFor(segment)"
            @click="(booking, rect) => !gridDrag.justDragged() && emit('event-click', booking, rect)"
            @drag-start="(e, mode, bk, seg) => gridDrag.start(e, mode, { booking: bk, segment: seg, column: column() })"
            @move-blocked="(reason) => emit('move-blocked', reason)"
          />

          <CalendarDragGhost :ghost="gridDrag.ghostFor(columnDayKey)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useCalendarStore }         from 'stores/calendar.js'
import { useCalendarSettingsStore } from 'stores/calendarSettings.js'
import { useGlobalStore }           from 'stores/global.js'
import { useRouter } from 'vue-router'
import { useTaskDates }        from 'composables/useTaskDates.js'
import { useCalendarGeometry, ROW_HEIGHT } from 'composables/useCalendarGeometry.js'
import { layoutDayEvents, segmentBookingsByDay, coversAllDay, dedupeAllDay } from 'utils/layoutEvents.js'
import { useGridDrag }       from 'composables/useGridDrag.js'
import CalendarEventCard       from './CalendarEventCard.vue'
import CalendarDragGhost       from './CalendarDragGhost.vue'
import CalendarCurrentTimeLine from './CalendarCurrentTimeLine.vue'
import CalendarTaskSummary     from './CalendarTaskSummary.vue'

const props = defineProps({
  selectedDate: { type: Date, required: true },
})

const emit = defineEmits(['event-click', 'slot-click', 'reschedule', 'move-blocked'])

const store         = useCalendarStore()
const settingsStore = useCalendarSettingsStore()
const globalStore   = useGlobalStore()
const router        = useRouter()
const gridEl        = ref(null)

const {
  dayKey, minutesOfDay, currentMinutesOfDay, dateAtMinutes, yToMinutes, snapMinutes,
  showSecondary, primaryTzLabel, secondaryTzLabel, hourLabels, secondaryHourLabels,
  darkenColor, readableTextColor,
} = useCalendarGeometry()

const today = new Date(); today.setHours(0, 0, 0, 0)

const dayMidnight = computed(() => {
  const d = new Date(props.selectedDate); d.setHours(0, 0, 0, 0); return d
})
const isToday      = computed(() => dayMidnight.value.getTime() === today.getTime())
const weekdayLabel = computed(() => props.selectedDate.toLocaleDateString('en-US', { weekday: 'short' }))
const dayNum       = computed(() => props.selectedDate.getDate())

const columnDayKey = computed(() => dayKey(new Date(dayMidnight.value.getTime() + 12 * 3600000)))

// Per-day segments (so a booking crossing midnight is clamped to this day) laid
// out into side-by-side columns instead of stacking on top of each other.
const daySegments = computed(() =>
  segmentBookingsByDay(store.filteredBookings, { dayKey, minutesOfDay }).get(columnDayKey.value) ?? []
)

const dayLayout = computed(() =>
  new Map(layoutDayEvents(daySegments.value).map(p => [p.key, p]))
)

function positionFor(segment) {
  return dayLayout.value.get(segment.key)
}

// Matched on calendar dates, not instants: all-day events carry an exclusive
// end date, and comparing them against local midnight rendered them a day too
// long for anyone away from UTC.
const allDayBookings = computed(() =>
  // Collapsed: the same holiday subscribed on several calendars is one event to
  // a reader, and stacking every copy buries the real ones.
  dedupeAllDay(store.filteredBookings.filter(b => b.all_day && coversAllDay(b, columnDayKey.value)))
)

// ── Tasks ─────────────────────────────────────────────────────────────────────

const { dateStrInUserTz } = useTaskDates()

function cellDateStr(date) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const dayTasks = computed(() => {
  const key = cellDateStr(dayMidnight.value)
  return store.filteredTasks.filter(t => dateStrInUserTz(t.due_at) === key)
})

function openTask(task) {
  router.push(`/tasks/${task.id}`)
}


function onSlotClick(event) {
  // A drag also fires a click on release; it has already been handled.
  if (gridDrag.isDragging.value || gridDrag.justDragged()) return
  const rect = event.currentTarget.getBoundingClientRect()
  emit('slot-click', dateAtMinutes(dayMidnight.value, snapMinutes(yToMinutes(event.clientY - rect.top))))
}

// ── Drag, drop and resize ─────────────────────────────────────────────────────

const columnEl = ref(null)
const column = () => ({ dayKey: columnDayKey.value, date: dayMidnight.value, el: columnEl.value })

// The column contains 48 absolutely-positioned rules that swallow a .self hit,
// so match on "not an event chip" instead.
function onGridPointerDown(event) {
  if (event.target.closest('[data-event-chip]')) return
  gridDrag.start(event, 'create', { column: column() })
}

const gridDrag = useGridDrag({
  dayColumns: () => [column()],
  scrollEl:   () => gridEl.value,
  onCreate:   ({ startMinutes, endMinutes }) => {
    emit('slot-click', dateAtMinutes(dayMidnight.value, startMinutes), dateAtMinutes(dayMidnight.value, endMinutes))
  },
  onCommit:   ({ booking, startMinutes, endMinutes }) => {
    emit('reschedule', booking, { dayKey: columnDayKey.value, startMinutes, endMinutes })
  },
})

// Local calendar date, not UTC — toISOString() shifts the day either side of
// UTC midnight.
function isoDate(date) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

async function fetchDay(date) {
  const str = isoDate(date)
  await Promise.all([store.fetchBookings(str, str), store.fetchTasks(str, str)])
}

onMounted(async () => {
  await Promise.all([globalStore.fetchCurrentUser(), fetchDay(props.selectedDate)])
  if (gridEl.value) {
    gridEl.value.scrollTop = Math.max(0, (currentMinutesOfDay() / 60) * ROW_HEIGHT - 2 * ROW_HEIGHT)
  }
})
watch(() => props.selectedDate, fetchDay)
</script>
